import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  BulkDeleteResponse,
  CreateEvaluationBody,
  CreateRunRequest,
  CreateRunResponse,
  EvaluationRecord,
  MeResponse,
  MetaPayload,
  ResultPayload,
  RunListResponse,
  SettingsResponse,
  UpdateEvaluationBody,
} from "../api/types"
import { apiFetch } from "./api"

// ---- Runs ----

export function useRunsList(params?: {
  limit?: number
  offset?: number
  status_filter?: string
  search?: string
  sort?: string
}) {
  return useQuery({
    queryKey: ["runs", params],
    queryFn: () =>
      apiFetch<RunListResponse>("/api/runs", {
        searchParams: {
          limit: params?.limit ?? 50,
          offset: params?.offset ?? 0,
          status_filter: params?.status_filter,
          search: params?.search,
          sort: params?.sort,
        },
      }),
  })
}

export function useRunResult(runId: string | undefined) {
  return useQuery({
    queryKey: ["runs", runId, "result"],
    queryFn: () => apiFetch<ResultPayload>(`/api/runs/${runId}`),
    enabled: !!runId,
  })
}

export function useRunMeta(runId: string | undefined) {
  return useQuery({
    queryKey: ["runs", runId, "meta"],
    queryFn: () => apiFetch<MetaPayload>(`/api/runs/${runId}/meta`),
    enabled: !!runId,
    // meta は status を含むので live 実行中は短めにポーリングする想定
    refetchInterval: (q) => {
      const d = q.state.data
      const status = d?.run?.status ?? d?.status
      return status === "running" || status === "queued" ? 5000 : false
    },
  })
}

export function useCreateRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateRunRequest) =>
      apiFetch<CreateRunResponse>("/api/runs", { method: "POST", body: req }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] })
    },
  })
}

/**
 * Re-run: POST /api/runs with `from_run_id`. Backend inherits inheritable
 * config keys (focus / proposers / model / etc.) from the original run.
 */
export function useRerunMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fromRunId: string) =>
      apiFetch<CreateRunResponse>("/api/runs", {
        method: "POST",
        body: { from_run_id: fromRunId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] })
    },
  })
}

export function useDeleteRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) => apiFetch<void>(`/api/runs/${runId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] })
    },
  })
}

export function useBulkDeleteRuns() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runIds: string[]) =>
      apiFetch<BulkDeleteResponse>("/api/runs", {
        method: "DELETE",
        body: { run_ids: runIds },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] })
    },
  })
}

// ---- Share (Phase 6 Stage C-D) ----

export interface ShareCreateResponse {
  run_id: string
  share_token: string
  share_path: string // "/shared/{token}"
}

export function useCreateShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) =>
      apiFetch<ShareCreateResponse>(`/api/runs/${encodeURIComponent(runId)}/share`, {
        method: "POST",
      }),
    onSuccess: (_data, runId) => {
      // ["runs", runId, "meta"] が useRunMeta の queryKey (上の useRunMeta:48 と一致)。
      // 旧 ["run", runId] だと meta refetch されず share_token が UI に反映されなかった。
      // result は share の影響を受けないので invalidate 不要。
      qc.invalidateQueries({ queryKey: ["runs", runId, "meta"] })
    },
  })
}

export function useRevokeShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) =>
      apiFetch<void>(`/api/runs/${encodeURIComponent(runId)}/share`, { method: "DELETE" }),
    onSuccess: (_data, runId) => {
      // 同上、useRunMeta の queryKey ["runs", runId, "meta"] に揃える
      qc.invalidateQueries({ queryKey: ["runs", runId, "meta"] })
    },
  })
}

export function useSharedRun(token: string | undefined) {
  return useQuery({
    queryKey: ["shared", token],
    queryFn: () => apiFetch<ResultPayload>(`/api/shared/${encodeURIComponent(token ?? "")}`),
    enabled: !!token,
  })
}

export function useSharedRunMeta(token: string | undefined) {
  return useQuery({
    queryKey: ["shared-meta", token],
    queryFn: () => apiFetch<MetaPayload>(`/api/shared/${encodeURIComponent(token ?? "")}/meta`),
    enabled: !!token,
  })
}

// ---- Settings ----

export function useSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: () => apiFetch<SettingsResponse>("/api/settings"),
  })
}

// Phase 5 D-2: primary scorer 候補を hard-code せず backend から動的取得。
export function useScorers() {
  return useQuery({
    queryKey: ["scorers"],
    queryFn: () => apiFetch<{ primary_candidates: string[] }>("/api/settings/scorers"),
    staleTime: 5 * 60 * 1000, // 5 分キャッシュ (頻繁に変わらない)
  })
}

// `useUpdateDefaults` / `useUpdateApiKeys` は Phase 6 cleanup で削除 (read-only 化)。
// API キー / defaults の変更は `.env` / `configs/default.yaml` (admin) 経由のみ。

// ---- Session (Phase 6 Supabase Auth) ----

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => apiFetch<MeResponse>("/api/me"),
    retry: false,
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ status: string }>("/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.clear()
      // 完全リロードで Cookie + state を確実にクリア → /login へ
      window.location.assign("/login")
    },
    // 失敗時の toast は呼び出し側 (Settings/AppBar) で mutate(undefined, { onError }) で個別対応
  })
}

// ---- Evaluations (型は api/types.ts に集約、ここでは re-export) ----

export type {
  CreateEvaluationBody,
  EvaluationEventType,
  EvaluationRating,
  EvaluationRecord,
  EvaluationSnapshot,
  UpdateEvaluationBody,
} from "../api/types"

export function useRunEvaluations(runId: string | undefined) {
  return useQuery({
    queryKey: ["evaluations", runId],
    queryFn: () =>
      apiFetch<EvaluationRecord[]>(`/api/runs/${encodeURIComponent(runId ?? "")}/evaluations`),
    enabled: !!runId,
  })
}

export function useCreateEvaluation(runId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateEvaluationBody) =>
      apiFetch<EvaluationRecord>("/api/evaluations", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evaluations", runId] })
    },
  })
}

export function useUpdateEvaluation(runId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ evaluation_id, body }: { evaluation_id: string; body: UpdateEvaluationBody }) =>
      apiFetch<EvaluationRecord>(`/api/evaluations/${encodeURIComponent(evaluation_id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evaluations", runId] })
    },
  })
}

export function useDeleteEvaluation(runId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (evaluation_id: string) =>
      apiFetch<void>(`/api/evaluations/${encodeURIComponent(evaluation_id)}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evaluations", runId] })
    },
  })
}
